import { CSSProperties, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  type College,
  DEFAULT_CONFIG,
  FeaturedIcon,
  getMarkdownComponents,
  ImageCard,
  InlineCtaCard,
  MeshGradient,
} from '@backrs_ui/common-ui';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { ClipboardStyled } from '@backrs_ui/grow-ui/src/components/clipboard/ClipboardStyled';
import { CopyButton } from '@backrs_ui/grow-ui/src/components/clipboard/CopyButton';
import { LubavButton } from '@backrs_ui/grow-ui/src/components/Lubav/LubavButton';
import type {
  CollegeShortlistBucket,
  CollegeShortlistCollege,
  CollegeShortlistPayload,
} from '@backrs_ui/grow-ui/src/components/Lubav/streamTypes';
import { AddIconChip } from '@backrs_ui/grow-ui/src/components/MyColleges/AddIconChip';
import { AddToMyCollegesButton } from '@backrs_ui/grow-ui/src/components/MyColleges/AddToMyCollegesButton';
import {
  TIER_COLORS,
  type TierColors,
} from '@backrs_ui/grow-ui/src/components/MyColleges/tierColors';
import { useSavedInstitutionIds } from '@backrs_ui/grow-ui/src/components/MyColleges/useMyCollegeToggle';
import { NuggetChip } from '@backrs_ui/grow-ui/src/components/Nuggets/NuggetChip';
import { useGetCollegeByInstitutionIdQuery } from '@backrs_ui/grow-ui/src/redux/college/college.api';
import { useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import { selectIsSharedSessionActive } from '@backrs_ui/grow-ui/src/redux/sharedSession/sharedSession';
import { useAddUserCollegeMutation } from '@backrs_ui/grow-ui/src/redux/userColleges/userColleges.api';

import { Box, Chip, Divider, Skeleton, SxProps, Theme, Typography } from '@mui/material';
import ReactMarkdown from 'react-markdown';

import { useAgentContext } from './CollegeShortlistRenderer.agentContext';

const STRINGS = appStrings.modules.officeHours.infoBox.collegeShortlist;

type Tier = Readonly<{
  key: CollegeShortlistBucket;
  label: string;
  detailTitle: string;
  colors: TierColors;
}>;

const TIERS: readonly Tier[] = [
  {
    key: 'safe',
    label: STRINGS.safety,
    detailTitle: STRINGS.safetySchools,
    colors: TIER_COLORS.safety,
  },
  {
    key: 'target',
    label: STRINGS.target,
    detailTitle: STRINGS.targetSchools,
    colors: TIER_COLORS.target,
  },
  {
    key: 'stretch',
    label: STRINGS.reach,
    detailTitle: STRINGS.reachSchools,
    colors: TIER_COLORS.reach,
  },
];

const HIGHLIGHT_COLORS: TierColors = {
  bg: 'Warning/50',
  border: 'Warning/200',
  text: 'Warning/700',
};

const IMAGE_COLUMN_WIDTH = 290; // Hardcoded: fixed image column from design

const useShortlistCollege = (school: CollegeShortlistCollege) => {
  const { data: college, isLoading } = useGetCollegeByInstitutionIdQuery(String(school.id), {
    skip: !school.id,
  });

  return { college, isLoading, name: college?.school_name || school.name || '' };
};

type CollegeBadgeProps = Readonly<{
  id: string;
  college: College | undefined;
  name: string;
  onClick?: () => void;
}>;

const CollegeBadge = ({ id, college, name, onClick }: CollegeBadgeProps) => (
  <NuggetChip
    id={id}
    label={<ClipboardStyled style={copyHeaderStyle}>{name}</ClipboardStyled>}
    image={{ src: college?.logo_media_url || '/images/university-logo-default.svg' }}
    onClick={onClick}
    sx={collegeBadgeSx}
  />
);

const SummaryRow = ({
  school,
  tier,
}: Readonly<{ school: CollegeShortlistCollege; tier: Tier }>) => {
  const { college, isLoading, name } = useShortlistCollege(school);

  if (isLoading) return <Skeleton variant="rounded" width={140} height={24} />;

  return (
    <Box sx={summaryRowSx}>
      <CollegeBadge
        id={`college-shortlist-chip-${school.id}`}
        college={college}
        name={name}
        onClick={() =>
          document
            .getElementById(`college-shortlist-detail-${school.id}`)
            ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      />
      {college && (
        <AddIconChip
          id={`college-shortlist-add-icon-${school.id}`}
          college={college}
          colors={tier.colors}
        />
      )}
    </Box>
  );
};

const LabeledMarkdown = ({ label, text }: Readonly<{ label: string; text: string }>) => (
  <Box sx={detailColumnSx}>
    <Chip variant="pill" size="sm" label={label} sx={tierChipSx(HIGHLIGHT_COLORS)} />
    <Box sx={markdownSx}>
      <ReactMarkdown components={getMarkdownComponents('SMALL')}>{text}</ReactMarkdown>
    </Box>
  </Box>
);

const SchoolDetail = ({ school }: Readonly<{ school: CollegeShortlistCollege }>) => {
  const navigate = useNavigate();
  const { college, isLoading, name } = useShortlistCollege(school);

  if (isLoading) return <Skeleton variant="rounded" height={260} />;

  return (
    <ClipboardStyled style={copySchoolSpacingStyle}>
      <Box id={`college-shortlist-detail-${school.id}`} sx={schoolDetailSx}>
        <ImageCard
          id={`college-shortlist-card-${school.id}`}
          imageUrl={college?.banner_media_url ?? ''}
          fallback={<MeshGradient config={DEFAULT_CONFIG} />}
          imageOverlay={
            <Box sx={imageOverlaySx}>
              <CollegeBadge
                id={`college-shortlist-card-chip-${school.id}`}
                college={college}
                name={name}
              />
            </Box>
          }
          onClick={() => college?.id && navigate(`${APP_ROUTE.UNIVERSITY}/${college.id}`)}
          sxOverrides={{ container: imageCardSx }}
        >
          {college && (
            <Box sx={cardActionSx} onClick={(event) => event.stopPropagation()}>
              <AddToMyCollegesButton
                college={college}
                id={`college-shortlist-add-${school.id}`}
                fullWidth
              />
            </Box>
          )}
        </ImageCard>
        <Box sx={detailColumnSx}>
          <LubavButton
            id={`college-shortlist-discuss-${school.id}`}
            label={STRINGS.discussFit}
            prompt={STRINGS.discussFitPrompt(name)}
            buttonSize="sm"
            sx={fitContentSx}
          />
          {school.relevance && (
            <Typography variant="text-sm-regular" sx={bodyTextSx}>
              {school.relevance}
            </Typography>
          )}
        </Box>
        {school.highlights && (
          <LabeledMarkdown label={STRINGS.highlights} text={school.highlights} />
        )}
        {school.considerations && (
          <LabeledMarkdown label={STRINGS.considerations} text={school.considerations} />
        )}
      </Box>
    </ClipboardStyled>
  );
};

type SaveAllCardProps = Readonly<{
  payload: CollegeShortlistPayload;
  savedIds: ReadonlySet<number> | undefined;
}>;

const SaveAllCard = ({ payload, savedIds }: SaveAllCardProps) => {
  const navigate = useNavigate();
  const [addUserCollege] = useAddUserCollegeMutation();
  const [isSaving, setIsSaving] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);

  const shortlistIds = new Set(
    TIERS.flatMap((tier) => payload[tier.key] ?? []).map((school) => Number(school.id)),
  );
  const unsaved = Array.from(shortlistIds).filter((id) => id && !savedIds?.has(id));

  const addAll = async () => {
    setIsSaving(true);
    setSaveFailed(false);
    const results = await Promise.allSettled(
      unsaved.map((id) => addUserCollege({ institution_id: id }).unwrap()),
    );

    setSaveFailed(results.some((result) => result.status === 'rejected'));
    setIsSaving(false);
  };

  return (
    <InlineCtaCard
      icon={<FeaturedIcon iconName="Zap" size="md" color="Colors/Foreground/fg-secondary" />}
      title={STRINGS.saveCard.title}
      supportingText={saveFailed ? STRINGS.saveCard.addAllFailed : STRINGS.saveCard.description}
      secondaryAction={{
        id: 'college-shortlist-view-my-colleges',
        label: STRINGS.saveCard.view,
        onClick: () => navigate(APP_ROUTE.MY_COLLEGES),
      }}
      primaryAction={{
        id: 'college-shortlist-add-all',
        label: savedIds && !unsaved.length ? STRINGS.saveCard.allAdded : STRINGS.saveCard.addAll,
        onClick: addAll,
        disabled: !savedIds || isSaving || !unsaved.length,
      }}
      stacked
    />
  );
};

type CollegeShortlistRendererProps = Readonly<{
  payload: CollegeShortlistPayload;
}>;

export const CollegeShortlistRenderer = ({ payload }: CollegeShortlistRendererProps) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const isSharedSession = useAppSelector(selectIsSharedSessionActive);
  const savedIds = useSavedInstitutionIds();

  useAgentContext(
    TIERS.flatMap((tier) =>
      (payload[tier.key] ?? []).map((school) => ({
        institution_id: school.id,
        name: school.name,
        tier: tier.label,
        in_my_colleges: savedIds?.has(Number(school.id)),
      })),
    ),
  );

  return (
    <Box ref={contentRef} sx={containerSx}>
      <Box sx={headerSx}>
        <Typography variant="display-sm-semibold">{STRINGS.title}</Typography>
        <CopyButton id="college-shortlist-copy-button" contentRef={contentRef} />
      </Box>
      <Box sx={summarySx}>
        <Box sx={summaryTiersSx}>
          {TIERS.map((tier) => (
            <Box key={tier.key} sx={summaryTierSx}>
              <Chip variant="pill" size="lg" label={tier.label} sx={tierChipSx(tier.colors)} />
              {payload[tier.key]?.map((school, i) => (
                <SummaryRow key={school.id ?? i} school={school} tier={tier} />
              ))}
            </Box>
          ))}
        </Box>
        {!isSharedSession && (
          <Box sx={saveAllSx} data-copy-exclude>
            <SaveAllCard payload={payload} savedIds={savedIds} />
          </Box>
        )}
      </Box>
      {TIERS.map((tier) => {
        const schools = payload[tier.key];

        if (!schools?.length) return null;

        const names = schools
          .map((school) => school.name)
          .filter(Boolean)
          .join(', ');

        return (
          <Box key={tier.key} sx={tierDetailSx}>
            <Box sx={tierHeadingSx}>
              <Box sx={tierDetailHeaderSx}>
                <Typography variant="display-xs-semibold">{tier.detailTitle}</Typography>
                <LubavButton
                  id={`college-shortlist-compare-${tier.key}`}
                  label={STRINGS.compare(tier.label)}
                  prompt={STRINGS.comparePrompt(tier.label, names)}
                />
              </Box>
              <Divider sx={dividerSx} />
            </Box>
            {schools.map((school, i) => (
              <SchoolDetail key={school.id ?? i} school={school} />
            ))}
          </Box>
        );
      })}
    </Box>
  );
};

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-4xl'],
  paddingBottom: backrs.spacing['spacing-4xl'],
});

const headerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: backrs.spacing['spacing-xl'],
});

const summarySx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: { xs: 'column', xl: 'row' },
  gap: backrs.spacing['spacing-3xl'],
  alignItems: 'flex-start',
});

const summaryTiersSx: SxProps<Theme> = ({ backrs }) => ({
  flex: 1,
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
  gap: backrs.spacing['spacing-3xl'],
  width: '100%',
});

const summaryTierSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: backrs.spacing['spacing-sm'],
  minWidth: 0,
});

const summaryRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-sm'],
  width: '100%',
  minWidth: 0,
});

const saveAllSx: SxProps<Theme> = ({ backrs }) => ({
  width: { xs: '100%', sm: backrs.width['width-xs'], xl: 280 }, // Hardcoded: xl width from design, between width tokens
  flexShrink: 0,
});

const tierChipSx =
  (colors: TierColors): SxProps<Theme> =>
  ({ backrs }) => ({
    backgroundColor: backrs.colors[colors.bg],
    borderColor: backrs.colors[colors.border],
    color: backrs.colors[colors.text],
  });

const collegeBadgeSx: SxProps<Theme> = {
  minWidth: 0,
};

const tierDetailSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-3xl'],
});

const tierDetailHeaderSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: backrs.spacing['spacing-lg'],
});

const tierHeadingSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
});

const dividerSx: SxProps<Theme> = ({ backrs }) => ({
  borderColor: backrs.colors['Gray/950'],
});

const schoolDetailSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'grid',
  gridTemplateColumns: {
    xs: 'minmax(0, 1fr)',
    sm: `minmax(0, ${IMAGE_COLUMN_WIDTH}px) minmax(0, 1fr)`,
    lg: `${IMAGE_COLUMN_WIDTH}px minmax(0, 1.3fr) minmax(0, 1fr) minmax(0, 1fr)`,
  },
  gap: backrs.spacing['spacing-2xl'],
  alignItems: 'start',
});

const imageCardSx: SxProps<Theme> = {
  width: '100%',
  gridRow: { sm: 'span 3', lg: 'auto' },
};

const imageOverlaySx: SxProps<Theme> = ({ backrs }) => ({
  position: 'absolute',
  display: 'flex',
  left: backrs.spacing['spacing-xl'],
  right: backrs.spacing['spacing-xl'],
  bottom: backrs.spacing['spacing-xl'],
});

const cardActionSx: SxProps<Theme> = {
  cursor: 'default',
};

const detailColumnSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: backrs.spacing['spacing-xl'],
  minWidth: 0,
});

const fitContentSx: SxProps<Theme> = {
  width: 'fit-content',
};

const bodyTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
});

const markdownSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
  '& ul, & ol': { margin: 0 },
  '& li:last-child': { marginBottom: 0 },
});

const copyHeaderStyle: CSSProperties = {
  fontWeight: '700',
  fontSize: '16px',
};

const copySchoolSpacingStyle: CSSProperties = {
  display: 'block',
  marginBottom: '16px',
};
